import React from 'react';
import { useTranslation } from 'react-i18next';

import { SelectLabel, Section, CheckboxLabel, CounterLabel, RadioLabel, TooltipHelp, OptionalSection } from '@weezevent/nacre';

import { Event, Rate } from '../../../../../../../models';

import css from '../../actions.module.css';

const AttendeeCreationAction = ({ organizationId, action, handleAction, handleActionObject }) => {
    const { t } = useTranslation();
    const [selectedEventId, setSelectedEventId] = React.useState(() => action.destination_event);

    const [events] = Event.useApiModel(
        {
            organizationId,
            query: 'publication_status__in=draft,published',
            cache: false,
            allow_cache: false,
            launch: Boolean(organizationId)
        },
        [organizationId]
    );

    const [rates, loadingRates] = Rate.useApiModel(
        {
            organizationId: organizationId,
            eventId: selectedEventId,
            launch: Boolean(organizationId && selectedEventId),
            cache: false,
            allow_cache: false
        },
        [organizationId, selectedEventId]
    );

    const eventsOptions = React.useMemo(() => events?.map(e => e.toOptions()) || [], [events]);

    const ratesOptions = React.useMemo(() => rates?.map(rate => rate.toOption()) || [], [rates]);

    return (
        <>
            <SelectLabel
                useNew
                value={action.destination_event}
                options={eventsOptions}
                onChange={value => {
                    handleAction('destination_event', value);
                    setSelectedEventId(value);
                }}
                label={t('ticket.moderation.statusesTransitions.transition.steps.actions.events_label')}
                placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.events_placeholder')}
            />

            <SelectLabel
                useNew
                value={action.destination_rate}
                disabled={(!selectedEventId && !rates) || loadingRates}
                options={rates ? ratesOptions : []}
                onChange={value => handleAction('destination_rate', value)}
                label={t('ticket.moderation.statusesTransitions.transition.steps.actions.rates_label')}
                placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.rates_placeholder')}
            />

            <div className={css['action-card-counter']}>
                <CounterLabel
                    min={1}
                    step={1}
                    max={100}
                    value={action.amount || 1}
                    onChange={(_, value) => handleAction('amount', value)}
                    label={t('ticket.moderation.statusesTransitions.transition.steps.actions.attendees_amount')}
                />
            </div>

            <OptionalSection
                label={t('ticket.moderation.statusesTransitions.transition.steps.actions.display_options')}
                labelOpen={t('ticket.moderation.statusesTransitions.transition.steps.actions.display_options_closed')}
            >
                <CheckboxLabel
                    label={
                        <span>
                            {t('ticket.moderation.statusesTransitions.transition.steps.actions.replayableActionLabel')}
                            <TooltipHelp triggerOnHover title={t('ticket.moderation.statusesTransitions.transition.steps.actions.replayableActionDesc')} />
                        </span>
                    }
                    onChange={({ target: { checked } }) => handleAction('allow_replay', checked)}
                    checked={action.allow_replay}
                />

                <CheckboxLabel
                    label={t('ticket.moderation.statusesTransitions.transition.steps.actions.attendee_informations')}
                    onChange={({ target: { checked } }) =>
                        handleActionObject({
                            ...action,
                            attendee_informations: checked,
                            keep_form_answers: checked,
                            keep_membership_code: checked ? action.keep_membership_code : false
                        })
                    }
                    checked={action.attendee_informations}
                />
                {action.attendee_informations && (
                    <Section className={css['action-section']}>
                        <CheckboxLabel
                            label={t('ticket.moderation.statusesTransitions.transition.steps.actions.form_answers')}
                            onChange={({ target: { checked } }) => handleAction('keep_form_answers', checked)}
                            checked={action.keep_form_answers}
                        />
                        <CheckboxLabel
                            label={t('ticket.moderation.statusesTransitions.transition.steps.actions.membership_code')}
                            onChange={({ target: { checked } }) => handleAction('keep_membership_code', checked)}
                            checked={action.keep_membership_code}
                        />
                    </Section>
                )}

                <div>
                    <div>{t('ticket.moderation.statusesTransitions.transition.steps.actions.destinationStatus', { count: Number(action.amount) || 0 })}</div>
                    <RadioLabel
                        bordered
                        inline
                        value="valid"
                        checked={action.destination_status === 'valid'}
                        onChange={({ target: { value } }) => handleAction('destination_status', value)}
                    >
                        {t('ticket.moderation.statusesTransitions.transition.steps.actions.destinationStatusValid')}
                    </RadioLabel>
                    <RadioLabel
                        bordered
                        inline
                        value="reservation"
                        checked={action.destination_status === 'reservation'}
                        onChange={({ target: { value } }) => handleAction('destination_status', value)}
                    >
                        {t('ticket.moderation.statusesTransitions.transition.steps.actions.destinationStatusReservation')}
                    </RadioLabel>
                </div>
            </OptionalSection>
        </>
    );
};

export default AttendeeCreationAction;
